import { useMemo, useRef, useEffect, useState } from 'react';
import { Search } from 'lucide-react';
import { BusinessCardContainer } from './BusinessCardContainer';
import { MobileBusinessListItem } from './MobileBusinessListItem';
import { Business } from '@/types/homepage';
import { useTranslation } from 'react-i18next';
import useHomePageStore from '@/store/useHomePageStore';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { useHomePage } from '@/components/homepage/HomePageContext';
import { cn } from '@/lib/utils';
import { BusinessCardsSkeletonGrid } from '@/components/ui/LoadingSpinner';

// Phones show a short list so the rest of the page stays within reach
const MOBILE_LIST_LIMIT = 5;

interface HomePageBusinessGridProps {
  filteredBusinesses: Business[];
  selectedBusiness: Business | null;
  businessActivities: Record<string, any[]>;
  onBusinessSelect: (business: Business | null) => void;
  onSearch: (query: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  onCustomAdventure?: (businessId: string) => void;
  isLoading?: boolean;
}

export function HomePageBusinessGrid({
  filteredBusinesses,
  selectedBusiness,
  businessActivities,
  onBusinessSelect,
  onSearch,
  searchInputRef,
  onCustomAdventure,
  isLoading = false
}: HomePageBusinessGridProps) {
  const { t } = useTranslation('public', { keyPrefix: 'homepage' });
  const {
    setSearchQuery,
    setActiveExperienceFilter,
    setSelectedAnimalTypes,
    setActivityFilters
  } = useHomePageStore();

  // Mobile detection and custom adventures
  const isMobile = useMediaQuery('(max-width: 768px)');
  const { businessesWithCustomAdventures } = useHomePage();

  const businessGridContainerRef = useRef<HTMLDivElement>(null);
  const [showFullList, setShowFullList] = useState(false);

  // 🔧 STABLE: Create stable business IDs hash with content-based comparison
  const businessIdsHash = useMemo(() => {
    const ids = filteredBusinesses.map(b => b._id).sort().join(',');
    return ids;
  }, [filteredBusinesses.map(b => b._id).sort().join(',')]);

  // 🎯 NEW: Smooth scroll to selected business (Desktop only)
  useEffect(() => {
    // Only scroll on desktop, not mobile
    if (isMobile || !selectedBusiness || !businessGridContainerRef.current) return;
    
    // Add small delay to ensure DOM is updated after business selection
    const scrollTimeout = setTimeout(() => {
      const container = businessGridContainerRef.current;
      if (!container) return;

      // Find the selected business card element
      const selectedCardElement = container.querySelector(`[data-business-id="${selectedBusiness._id}"]`);
      
      if (selectedCardElement) {
        selectedCardElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'nearest'
        });
      }
    }, 150); // Small delay to ensure the selection state has been applied

    return () => clearTimeout(scrollTimeout);
  }, [selectedBusiness, isMobile, businessIdsHash]); // Re-run when business selection changes

  const MemoizedBusinessCards = useMemo(() => {
    const listedBusinesses = isMobile && !showFullList
      ? filteredBusinesses.slice(0, MOBILE_LIST_LIMIT)
      : filteredBusinesses;

    return listedBusinesses.map((business) => {
      if (isMobile) {
        return (
          <div key={business._id} data-business-id={business._id}>
            <MobileBusinessListItem
              business={business}
              activities={businessActivities[business._id] || []}
              onSelect={onBusinessSelect}
              onCustomAdventure={onCustomAdventure}
              hasCustomAdventures={businessesWithCustomAdventures.has(business._id)}
              className="mb-2"
            />
          </div>
        );
      }

      return (
        <div key={business._id} data-business-id={business._id}>
          <BusinessCardContainer
            business={business}
            onSelect={onBusinessSelect}
          />
        </div>
      );
    });
  }, [
    businessIdsHash,
    selectedBusiness?._id,
    Object.keys(businessActivities).length,
    onBusinessSelect,
    onCustomAdventure,
    isMobile,
    showFullList,
    businessesWithCustomAdventures,
  ]);

  return (
    <>
      {/* ✨ NEW: Show loading skeletons when loading */}
      {isLoading && filteredBusinesses.length === 0 ? (
        <BusinessCardsSkeletonGrid count={3} />
      ) : filteredBusinesses.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="bg-gray-100 p-6 rounded-full mb-4">
            <Search className="h-8 w-8 text-gray-400" />
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">{t('businesses.empty.title')}</h3>
          <p className="text-gray-600 max-w-md mb-6">
            {t('businesses.empty.description')}
          </p>
          <div className="flex flex-wrap gap-2 justify-center">
            {([
              { label: t('businesses.empty.suggestions.norway'), query: 'Norway' },
              { label: t('businesses.empty.suggestions.sweden'), query: 'Sweden' },
              { label: t('businesses.empty.suggestions.spain'), query: 'Spain' },
            ] as const).map(({ label, query }) => (
              <button
                key={query}
                onClick={() => {
                  setSearchQuery('');
                  if (searchInputRef.current) searchInputRef.current.value = '';
                  setActiveExperienceFilter(null);
                  setSelectedAnimalTypes([]);
                  setActivityFilters({
                    showAnimalActivities: true,
                    showAdventureActivities: true
                  });
                  onSearch(query);
                }}
                className="px-4 py-2 min-h-[44px] bg-white border border-gray-200 rounded-full text-sm font-medium text-gray-700 hover:bg-primary hover:text-white transition-colors duration-200 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 touch-manipulation"
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div 
          ref={businessGridContainerRef} 
          className={cn(
            "flex flex-col custom-scrollbar-dark",
            // Mobile: Tighter spacing and better scroll behavior
            isMobile ? "space-y-2 pb-6" : "space-y-4 p-4"
          )}
        >
          {MemoizedBusinessCards}
          {isMobile && !showFullList && filteredBusinesses.length > MOBILE_LIST_LIMIT && (
            <button
              type="button"
              onClick={() => setShowFullList(true)}
              className="w-full min-h-[44px] rounded-lg border border-primary bg-white text-primary text-sm font-semibold touch-manipulation focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
            >
              {t('businesses.showAll', { count: filteredBusinesses.length })}
            </button>
          )}
        </div>
      )}
    </>
  );
} 